import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router'
import { useCurrentUserCacheOnly } from 'shared/data'
import { ModuleRoute } from 'shared/types'
import { paths } from 'config'
import { ViewerMode, viewerModeVar } from 'shared/utils'
import { useAuthContext } from '../AuthContext'

export const useCurrentRoute = (routes: ModuleRoute[]) => {
  const { pathname } = useLocation<{ background?: Location } | null>()
  return routes.find(routeProps => matchPath(pathname, routeProps))
}

export const useCurrentLayoutSettings = (routes: ModuleRoute[]) =>
  useCurrentRoute(routes)?.layoutSettings || {}

const filterRoutes = (
  routes: ModuleRoute[],
  options: {
    isAdmin: boolean | null | undefined
    isSuperAdmin?: boolean
    canShowVisitorCheckInRequests: boolean
    canShowOrganizations: boolean | null | undefined
    canSeeProfiles: boolean
  }
): ModuleRoute[] => {
  return routes.filter(r => {
    if (r.canShowOrganizations) {
      if (options.canShowOrganizations) return true
      return false
    }
    if (r.isSuperAdmin) {
      if (options.isSuperAdmin) return true
      return false
    }

    if (r.admin) {
      if (options.isAdmin) return true
      if (
        options.canSeeProfiles &&
        (r.path === paths.profiles || r.path.startsWith(paths.profiles + '/'))
      ) {
        return true
      }
      return false
    }
    if (r.canShowVisitorPanelCheckIns) {
      if (options.canShowVisitorCheckInRequests) return true
      return false
    }

    return true
  })
}

export const useAvailableRoutes = (routes: ModuleRoute[]) => {
  const { user } = useAuthContext()
  const { data, loading } = useCurrentUserCacheOnly()
  // Signed in, but the user's data isn't in the cache yet, e.g. right after sign-in.
  // Until it is, only the non-admin routes are known.
  const isUserDataPending = !!user && !data

  const nonAdminRoutes = filterRoutes(routes, {
    isAdmin: false,
    canShowVisitorCheckInRequests: false,
    canSeeProfiles: false,
    canShowOrganizations: false,
  })

  const availableRoutes = useMemo(() => {
    if (!data) return nonAdminRoutes

    const isAdmin = data.viewer.isAdmin
    const isSuperAdmin = viewerModeVar() === ViewerMode.SYSTEM_ADMIN
    const canShowVisitorCheckInRequests =
      data.viewer.viewerCanSeeVisitorCheckInRequests
    const canShowOrganizations = data.viewer.viewerCanSeeOrganizations
    const canSeeLocalNetworks = !!data.viewer.viewerCanSeeLocalNetworks

    const accessProfilesCount = data.viewer.accessProfiles?.totalCount || 0
    const canSeeProfiles = isAdmin || accessProfilesCount > 0
    const filtered = filterRoutes(routes, {
      isAdmin,
      isSuperAdmin,
      canShowVisitorCheckInRequests,
      canSeeProfiles,
      canShowOrganizations,
    })

    return filtered.map(route =>
      route.path === paths.lanMonitoring && !canSeeLocalNetworks
        ? { ...route, linkSettings: undefined }
        : route
    )
  }, [data, routes, nonAdminRoutes])

  return { availableRoutes, loading, isUserDataPending }
}
